import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_TASK_SUGGESTIONS = [
  'Follow up call',
  'Send information',
  'Schedule meeting',
  'WhatsApp follow up',
  'Check payment status'
];

export function LeadQuickTaskModal({ open, onClose, lead, onTaskCreated }) {
  const [taskData, setTaskData] = useState({
    title: '', description: '', due_date: '', priority: 'normal'
  });

  const handleCreate = async () => {
    if (!lead) return;
    if (!taskData.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    if (!taskData.due_date) {
      toast.error('Due date is required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: taskData.title,
          description: taskData.description || `Task for lead: ${lead.first_name} ${lead.last_name || ''}`.trim(),
          due_date: new Date(taskData.due_date).toISOString(),
          priority: taskData.priority,
          task_type: 'Custom Task',
          related_lead_id: lead.lead_id,
          task_source: 'self_assigned'
        }),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Task created successfully');
        setTaskData({ title: '', description: '', due_date: '', priority: 'normal' });
        onClose();
        onTaskCreated?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      logger.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleClose = () => {
    setTaskData({ title: '', description: '', due_date: '', priority: 'normal' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-md" data-testid="quick-task-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlusCircle size={18} className="text-green-500" />
            Add Task for {lead?.first_name} {lead?.last_name || ''}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Label className="text-sm font-medium">Task Title <span className="text-red-500">*</span></Label>
            <Input
              value={taskData.title}
              onChange={(e) => setTaskData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Follow up call"
              className="mt-1"
              data-testid="quick-task-title"
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Description</Label>
            <Textarea
              value={taskData.description}
              onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Additional details..."
              className="mt-1 min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Due Date <span className="text-red-500">*</span></Label>
              <Input
                type="datetime-local"
                value={taskData.due_date}
                onChange={(e) => setTaskData(prev => ({ ...prev, due_date: e.target.value }))}
                className="mt-1"
                data-testid="quick-task-due-date"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Priority</Label>
              <Select
                value={taskData.priority}
                onValueChange={(v) => setTaskData(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quick Task Suggestions */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Quick tasks:</p>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_TASK_SUGGESTIONS.map((task) => (
                <Button
                  key={task}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 px-2 hover:bg-green-50 hover:text-green-700 hover:border-green-200"
                  onClick={() => setTaskData(prev => ({
                    ...prev,
                    title: `${task}: ${lead?.first_name || ''} ${lead?.last_name || ''}`.trim()
                  }))}
                >
                  {task}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!taskData.title.trim() || !taskData.due_date}
            className="bg-green-600 hover:bg-green-700"
            data-testid="quick-task-submit"
          >
            Create Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
